/**
 * financialFormat — the SINGLE source of truth for how a monetary/percent figure
 * becomes a display STRING, per the ClaimGenius black+orange design law.
 *
 * Design law (docs/plans/2026-07-24-k3-black-orange-design-law.md §3/§4;
 * 2026-06-25-redesign-design-spec.md §1/§2):
 *  - whole dollars, thousands-separated, NEVER abbreviated on output ($1.2M → $1,200,000)
 *  - negative / contra → magnitude in parentheses  ($12,345)
 *  - value === 0            → em-dash "—"  (VERIFIED ZERO: the engine ran, answer is 0)
 *  - value == null/undefined → "NOT RUN"  (absence of a run, NEVER conflated with zero)
 *  - backend strings are normalized (strip $ , () %), abbreviations passed through raw
 *
 * This is a PURE function of the value — it hardcodes no figure. `<FinancialValue>`
 * renders from `formatFinancial` and layers on mono/align/color/chip presentation;
 * string call-sites (chart axis labels, tooltips, sentences, exports) use
 * `formatCurrency`. Both share THIS rule so the two can never drift.
 */

export type FinancialState = 'value' | 'zero' | 'notRun' | 'unnormalized';

export interface FinancialFormat {
  /** Which display state the value resolved to. */
  state: FinancialState;
  /** The display text for this state ("$1,234", "($1,234)", "—", "NOT RUN", or raw). */
  text: string;
  /** True only in the 'value' state when the figure is negative (for color logic). */
  isNegative: boolean;
}

/** Abbreviated money like "1.2M", "$3.4k", "(2B)" — never expanded into invented precision. */
const ABBREVIATION_RE = /^\(?\s*-?\s*\$?\s*\d[\d,]*(?:\.\d+)?\s*[KkMmBbTt]n?\s*\)?$/;

export interface FinancialFormatOptions {
  /** Render as a percentage instead of currency. */
  percent?: boolean;
  /** Contra/deduction row — show magnitude in parens even when positive (accounting convention). */
  contra?: boolean;
  /**
   * Penny precision (2 decimals) for the VALUE state only — for per-transaction
   * audit surfaces (ledger rows, transaction tables) where the cent must be
   * visible to reconcile. Whole-dollar (0 decimals) is the default and the law
   * for every computed/summary figure (dashboards, reports, settlement, the P&L
   * reconciliation grid). `cents` changes ONLY the fraction digits of a nonzero
   * currency value: the zero→em-dash, null→NOT RUN, and negative→parens rules
   * are unchanged, and it has no effect in percent mode. Design law renders money
   * whole-dollar; this is the explicit per-transaction exception (owner, 2026-08-28).
   */
  cents?: boolean;
}

/**
 * Resolve any value (number, backend string, null) to its design-law display state + text.
 * Structured so the component can style each state and string callers can take `.text`.
 */
export function formatFinancial(
  value: number | string | null | undefined,
  { percent = false, contra = false, cents = false }: FinancialFormatOptions = {},
): FinancialFormat {
  let numericValue: number | null | undefined;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      numericValue = null; // empty string is "no value", not zero
    } else if (ABBREVIATION_RE.test(trimmed)) {
      return { state: 'unnormalized', text: trimmed, isNegative: false };
    } else {
      const stringWasParenNegative = /^\(.*\)$/.test(trimmed);
      const cleaned = trimmed.replace(/[($),\s]/g, '').replace(/%$/, '');
      const parsed = Number(cleaned);
      if (cleaned !== '' && Number.isFinite(parsed)) {
        numericValue = stringWasParenNegative ? -Math.abs(parsed) : parsed;
      } else {
        return { state: 'unnormalized', text: trimmed, isNegative: false };
      }
    }
  } else {
    numericValue = value;
  }

  if (numericValue === null || numericValue === undefined) {
    return { state: 'notRun', text: 'NOT RUN', isNegative: false };
  }

  const num = Number(numericValue);
  if (num === 0) {
    return { state: 'zero', text: '—', isNegative: false };
  }

  const isNegative = num < 0;
  const inParens = isNegative || contra;
  const abs = Math.abs(num);

  let formatted: string;
  if (percent) {
    const pctValue = abs < 1 ? abs * 100 : abs; // ratio (<1) → percentage
    formatted = `${pctValue.toFixed(1)}%`;
  } else {
    // Whole-dollar is the law; `cents` (per-transaction audit surfaces only) opts into 2 decimals.
    const digits = cents ? 2 : 0;
    // THIS is the one place the canonical money string is built — every OTHER
    // inline $-toLocaleString is the drift the currencyFormat ratchet catches.
    formatted = `$${abs.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`; // design-law-allow
  }
  if (inParens) formatted = `(${formatted})`;

  return { state: 'value', text: formatted, isNegative };
}

/**
 * Design-law currency/percent STRING for non-JSX contexts (chart labels, tooltips,
 * sentences, exports). Returns the em-dash for a verified zero and "NOT RUN" for a
 * missing value — pass `zeroText` / `notRunText` to override for a specific context
 * (e.g. an axis label may want "$0" or ""). Prefer <FinancialValue> for rendered UI.
 */
export function formatCurrency(
  value: number | string | null | undefined,
  opts: FinancialFormatOptions & { zeroText?: string; notRunText?: string } = {},
): string {
  const { zeroText, notRunText, ...formatOpts } = opts;
  const r = formatFinancial(value, formatOpts);
  if (r.state === 'zero' && zeroText !== undefined) return zeroText;
  if (r.state === 'notRun' && notRunText !== undefined) return notRunText;
  return r.text;
}

// ── WP14: "not computed" vs real zero (owner rule 2026-11-01) ───────────────────────────
// On the WP14 surfaces a REAL computed zero reads "$0.00" and a missing value reads
// "Not computed" — never the silent `x || 0` that made the two look alike. Routed ONLY
// through formatMoneyOrNotComputed / <FinancialValue zeroLabel notRunLabel>; the defaults of
// formatCurrency / formatFinancial / <FinancialValue> for every other screen are unchanged.
export const NOT_COMPUTED_TEXT = 'Not computed';
export const ZERO_TEXT = '$0.00'; // design-law-allow: owner WP14 rule — real zero reads $0.00 on these surfaces
export const NOT_ON_FILE_TEXT = 'Not on file';

export function formatMoneyOrNotComputed(
  value: number | string | null | undefined,
  opts: FinancialFormatOptions = {},
): string {
  return formatCurrency(value, { ...opts, zeroText: ZERO_TEXT, notRunText: NOT_COMPUTED_TEXT });
}
